import cx from 'clsx';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

import { useIsomorphicLayoutEffect } from '@placekit/uikit/features/useIsomorphicLayoutEffect';

// Longest transition-duration + transition-delay of an element, in ms.
const transitionMs = (el) => {
  const style = window.getComputedStyle(el);
  const toMs = (value) =>
    Math.max(
      0,
      ...value.split(',').map((v) => parseFloat(v) * (v.trim().endsWith('ms') ? 1 : 1000) || 0),
    );
  return toMs(style.transitionDuration) + toMs(style.transitionDelay);
};

/**
 * Minimal CSS class transition (drop-in for the Headless UI v1 `Transition` the site used).
 * Mounts a `<div>` when `show` turns true with `enter enterFrom`, switches to `enter enterTo` after two frames,
 * drops them when the transition ends; on hide applies `leave leaveFrom`, then `leave leaveTo`, then unmounts.
 */
const Transition = ({
  show,
  appear = false,
  enter,
  enterFrom,
  enterTo,
  leave,
  leaveFrom,
  leaveTo,
  beforeEnter,
  className,
  children,
  ...rest
}) => {
  const ref = useRef(null);
  const prevShow = useRef(show);
  const appeared = useRef(false);
  // latest `show`, read by the stage effect to ignore a completion that `show` already contradicts
  // (updated at commit, before any browser event can run)
  const showRef = useRef(show);
  const beforeEnterRef = useRef(beforeEnter);
  const [mounted, setMounted] = useState(show);
  const [stage, setStage] = useState(show && appear ? 'enterFrom' : 'idle');

  useIsomorphicLayoutEffect(() => {
    showRef.current = show;
  }, [show]);

  useEffect(() => {
    beforeEnterRef.current = beforeEnter;
  }, [beforeEnter]);

  // `appear`: enter on mount when `show` is already true (guarded for StrictMode double effects)
  useEffect(() => {
    if (show && appear && !appeared.current) {
      appeared.current = true;
      beforeEnterRef.current?.();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Start entering or leaving when `show` changes (compared to its previous value, so StrictMode
  // double effects do not replay a transition).
  useEffect(() => {
    if (prevShow.current === show) return;
    prevShow.current = show;
    if (show) {
      beforeEnterRef.current?.();
      setMounted(true);
      setStage('enterFrom');
    } else {
      setStage('leaveFrom');
    }
  }, [show]);

  // Advance the stages: *From -> *To after a double rAF, *To -> done on transitionend (or a timeout fallback).
  useEffect(() => {
    const el = ref.current;
    if (!el || stage === 'idle') return;
    const done = () => {
      // `show` flipped before this stage ended: its own effect has already queued the next stage
      if (
        (stage.startsWith('enter') && !showRef.current) ||
        (stage.startsWith('leave') && showRef.current)
      ) {
        return;
      }
      setStage('idle');
      if (stage === 'leaveTo' || stage === 'leaveFrom') setMounted(false);
    };
    if (stage === 'enterFrom' || stage === 'leaveFrom') {
      // Not rendered (e.g. display: none): no transition can run, finish right away.
      if (!el.getClientRects().length) return done();
      let raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => setStage(stage === 'enterFrom' ? 'enterTo' : 'leaveTo'));
      });
      return () => cancelAnimationFrame(raf);
    }
    const onEnd = (e) => e.target === el && done();
    const timeout = setTimeout(done, transitionMs(el) + 50);
    el.addEventListener('transitionend', onEnd);
    return () => {
      clearTimeout(timeout);
      el.removeEventListener('transitionend', onEnd);
    };
  }, [stage]);

  if (!mounted) return null;
  const classes = {
    idle: className,
    enterFrom: cx(className, enter, enterFrom),
    enterTo: cx(className, enter, enterTo),
    leaveFrom: cx(className, leave, leaveFrom),
    leaveTo: cx(className, leave, leaveTo),
  }[stage];
  return (
    <div ref={ref} className={classes || undefined} {...rest}>
      {children}
    </div>
  );
};

Transition.propTypes = {
  /** Mounts and enters when true, leaves then unmounts when false */
  show: PropTypes.bool.isRequired,
  /** Run the enter transition on first render when `show` is already true */
  appear: PropTypes.bool,
  enter: PropTypes.string,
  enterFrom: PropTypes.string,
  enterTo: PropTypes.string,
  leave: PropTypes.string,
  leaveFrom: PropTypes.string,
  leaveTo: PropTypes.string,
  /** Called right before entering */
  beforeEnter: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default Transition;
